import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Maximize2, Minimize2, Video, Search } from 'lucide-react';
import { getAccessToken } from '../services/auth';

const DW_BASE = window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions/dw-spectrum-proxy'
  : '/.netlify/functions/dw-spectrum-proxy';

// The camera proxy needs a CPx session (Oct 23, 2026).
function dwPost(body) {
  const token = getAccessToken();
  return fetch(DW_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: JSON.stringify(body),
  });
}

function CameraThumbnail({ camera, onClick, size }) {
  const [src, setSrc] = useState(null);
  const [error, setError] = useState(false);

  const loadThumb = useCallback(() => {
    setError(false);
    dwPost({ action: 'thumbnail', cameraId: camera.id, time: 'latest' })
      .then(r => {
        if (!r.ok) throw new Error('Failed');
        return r.blob();
      })
      .then(blob => setSrc(URL.createObjectURL(blob)))
      .catch(() => setError(true));
  }, [camera.id]);

  useEffect(() => {
    loadThumb();
    const interval = setInterval(loadThumb, 30000); // refresh every 30s
    return () => {
      clearInterval(interval);
      if (src) URL.revokeObjectURL(src);
    };
  }, [camera.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isLarge = size === 'large';

  return (
    <div
      onClick={onClick}
      style={{
        position: 'relative',
        borderRadius: 8,
        overflow: 'hidden',
        background: '#1a1a2e',
        cursor: 'pointer',
        aspectRatio: '16/9',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid var(--gray-700, #333)',
      }}
    >
      {src && !error ? (
        <img
          src={src}
          alt={camera.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : error ? (
        <div style={{ color: '#666', fontSize: 12, textAlign: 'center', padding: 16 }}>
          <Video size={24} style={{ opacity: 0.3, marginBottom: 4 }} /><br />
          No preview
        </div>
      ) : (
        <div style={{ color: '#666', fontSize: 12 }}>Loading...</div>
      )}
      {/* Camera name overlay */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
        padding: isLarge ? '16px 12px 8px' : '12px 8px 4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <span style={{
          color: '#fff',
          fontSize: isLarge ? 13 : 11,
          fontWeight: 600,
          textShadow: '0 1px 3px rgba(0,0,0,0.8)',
        }}>
          {camera.name}
        </span>
        <div style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: camera.status === 'Recording' ? '#22c55e' : camera.status === 'Online' ? '#3b82f6' : '#ef4444',
          flexShrink: 0,
        }} />
      </div>
    </div>
  );
}

export default function Cameras() {
  const [cameras, setCameras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [expandedCamera, setExpandedCamera] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadCameras = useCallback(() => {
    setLoading(true);
    setError(null);
    dwPost({ action: 'cameras' })
      .then(r => r.json())
      .then(data => {
        const cams = (Array.isArray(data) ? data : []).map(d => ({
          id: d.id,
          name: d.name,
          physicalId: d.mac || d.physicalId || d.id,
          status: d.status || 'Unknown',
          model: d.model || '',
        }));
        cams.sort((a, b) => a.name.localeCompare(b.name));
        setCameras(cams);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadCameras(); }, [loadCameras]);

  const filtered = cameras.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name.toLowerCase().includes(q) || c.model.toLowerCase().includes(q);
  });

  const recording = cameras.filter(c => c.status === 'Recording').length;
  const offline = cameras.filter(c => c.status !== 'Recording' && c.status !== 'Online').length;

  if (expandedCamera) {
    const cam = cameras.find(c => c.id === expandedCamera);
    if (!cam) { setExpandedCamera(null); return null; }
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Video size={20} /> {cam.name}
          </h1>
          <button
            className="btn btn-secondary"
            onClick={() => setExpandedCamera(null)}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <Minimize2 size={14} /> Back to Grid
          </button>
        </div>
        <div style={{ maxWidth: 1200 }}>
          <CameraThumbnail key={`${cam.id}-${refreshKey}`} camera={cam} onClick={() => {}} size="large" />
        </div>
        <div style={{ marginTop: 12, display: 'flex', gap: 16, fontSize: 13, color: 'var(--gray-400)' }}>
          <span><strong>Status:</strong> {cam.status}</span>
          <span><strong>Model:</strong> {cam.model}</span>
          <span><strong>ID:</strong> {cam.id}</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Video size={20} /> Cameras
          </h1>
          <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 2 }}>
            DW Spectrum — {cameras.length} cameras
            {recording > 0 && <span style={{ color: '#22c55e', marginLeft: 8 }}>{recording} recording</span>}
            {offline > 0 && <span style={{ color: '#ef4444', marginLeft: 8 }}>{offline} offline</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)' }} />
            <input
              type="text"
              placeholder="Search cameras..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                padding: '6px 8px 6px 28px',
                borderRadius: 6,
                border: '1px solid var(--gray-700, #333)',
                background: 'var(--gray-800, #1e1e1e)',
                color: 'inherit',
                fontSize: 13,
                width: 180,
              }}
            />
          </div>
          <button
            className="btn btn-secondary"
            onClick={() => { setRefreshKey(k => k + 1); loadCameras(); }}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px' }}
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {loading && !cameras.length ? (
        <div style={{ textAlign: 'center', padding: 48, color: 'var(--gray-400)' }}>
          Loading cameras...
        </div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#ef4444' }}>
          Error: {error}
          <br />
          <button className="btn btn-secondary" onClick={loadCameras} style={{ marginTop: 12 }}>
            Retry
          </button>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 12,
        }}>
          {filtered.map(cam => (
            <CameraThumbnail
              key={`${cam.id}-${refreshKey}`}
              camera={cam}
              onClick={() => setExpandedCamera(cam.id)}
            />
          ))}
          {filtered.length === 0 && (
            <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 48, color: 'var(--gray-400)' }}>
              No cameras match "{search}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}
